import { Button, Grid, Paper, Typography } from "@mui/material"
import { Title } from "components/Common/Title"
import { UserReportDialog } from "components/Report/UserReportDialog"
import { ExamList } from "components/TrainingReportList/ExamList"
import { TrainingChart } from "components/TrainingReportList/TrainingChart"
import { TrainingReportList } from "components/TrainingReportList/TrainingReportList"
import { useAppDispatch, useAppSelector } from "hooks"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import lessonService from "services/lessonService"
import organizationService from "services/organizationService"
import trainingReportService from "services/trainingReportService"
import userService from "services/userService"
import { setLessons } from "slices/lessonSlice"
import { setCurrentOrganization } from "slices/organizationSlice"
import { setTrainingReports } from "slices/trainingReportSlice"
import { setSelectedUser } from "slices/usersSlice"
import { LessonType } from "types/lessonTypes"
import TimeSpan from "types/timeSpan"
import type { ITrainingReport } from "types/trainingReportTypes"

export function User() {
  const dispatch = useAppDispatch()

  const currentOrganization = useAppSelector((state) => state.organization.currentOrganization)
  const selectedUser = useAppSelector((state) => state.users.selectedUser)
  const trainingReports = useAppSelector((state) => state.trainingreport.trainingReports)

  const lessons = useAppSelector((state) => state.lesson.lessons)
  const { id, userId } = useParams()

  const [userReportDialogOpen, setUserReportDialogOpen] = useState(false)

  const handleUserReportDialogOpen = (open: boolean) => {
    setUserReportDialogOpen(open)
  }

  useEffect(() => {
    if (currentOrganization === undefined && id) {
      organizationService
        .getOrganization(id)
        .then((organization) => {
          dispatch(setCurrentOrganization(organization))
        })
        .catch((err) => {
          console.log(err)
        })
    }

    if (userId && (selectedUser === undefined || (selectedUser && selectedUser.id !== userId))) {
      userService
        .getUser(userId)
        .then((user) => {
          dispatch(setSelectedUser(user))

          trainingReportService
            .getUserTrainingReports(user.id)
            .then((reports) => {
              dispatch(setTrainingReports(reports))
            })
            .catch((err) => {
              console.log(err)
            })
        })
        .catch((err) => {
          console.log(err)
        })
    }

    if (id && lessons.length < 1) {
      lessonService
        .getLessons()
        .then((lessons) => {
          dispatch(setLessons(lessons))
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [currentOrganization, dispatch, id, lessons.length, selectedUser, userId])

  const totalTrainingTime = (trainingReports: ITrainingReport[]): string => {
    const totalMilliseconds = trainingReports.reduce((accumulator, object) => {
      return accumulator + new Date(object.endDate).getTime() - new Date(object.startDate).getTime()
    }, 0)

    const timespan = new TimeSpan(totalMilliseconds)

    if (timespan.hours < 1) {
      return `${timespan.minutes}min`
    }

    return `${timespan.hours}h  ${timespan.minutes}min`
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 220 }}>
          {selectedUser && (
            <Grid container spacing={1}>
              <Grid item xs>
                <Title>{`${selectedUser.firstname} ${selectedUser.lastname}`}</Title>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleUserReportDialogOpen(true)
                  }}
                >
                  GENERATE REPORT
                </Button>
                <UserReportDialog
                  userId={selectedUser.id}
                  open={userReportDialogOpen}
                  handleClose={() => handleUserReportDialogOpen(false)}
                />
              </Grid>
              <Grid item container marginTop={2}>
                <Grid item xs={1}>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                </Grid>
                <Grid item xs>
                  <Typography variant="body2" color="text.primary">
                    {selectedUser.role === "OrganizationAdmin" ? "Admin" : "User"}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container>
                <Grid item xs={1}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                </Grid>
                <Grid item xs>
                  <Typography variant="body2" color="text.primary">
                    {selectedUser.email ? selectedUser.email : "-"}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container>
                <Grid item xs={1}>
                  <Typography variant="body2" color="text.secondary">
                    Phone
                  </Typography>
                </Grid>
                <Grid item xs>
                  <Typography variant="body2" color="text.primary">
                    {selectedUser.phonenumber ? selectedUser.phonenumber : "-"}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item container>
                <Grid item xs={1}>
                  <Typography variant="body2" color="text.secondary">
                    PIN
                  </Typography>
                </Grid>
                <Grid item xs>
                  <Typography variant="body2" color="text.primary">
                    {selectedUser.pin}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
      <Grid item xs={3}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 220 }}>
          {selectedUser && (
            <Grid>
              <Grid item>
                <Title>TOTAL TRAINING TIME</Title>
              </Grid>
              <Grid container justifyContent="center" alignItems="flex-end" marginTop={5}>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: 42,
                  }}
                >
                  {totalTrainingTime(trainingReports)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
      <Grid item xs={3}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 220 }}>
          {selectedUser && (
            <Grid>
              <Grid item>
                <Title>COMPLETED LESSONS</Title>
              </Grid>
              <Grid container justifyContent="center" alignItems="flex-end" marginTop={2}>
                <Typography variant="h1">{trainingReports.length}</Typography>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: 300,
          }}
        >
          <Title>TRAINING ACTIVITY</Title>
          <Grid item xs={12}>
            <TrainingChart trainingReports={trainingReports} />
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Title>EXAMS</Title>
          <Grid item xs={12}>
            <ExamList
              trainingReports={trainingReports.filter((x) => {
                const lesson = lessons.find((y) => y.id === x.lessonId)
                return lesson !== undefined && lesson.type === LessonType.Exam
              })}
            />
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Title>TRAINING HISTORY</Title>
          <Grid item xs={12}>
            <TrainingReportList trainingReports={trainingReports} />
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}
